import { ChevronDownIcon } from '@heroicons/react/24/outline';
import TextWinds from '../TextWinds';
import HFbutton from '../../button/HFbutton';

/** 영역 헤더의 수정/저장 상태와 동작. 화면이 영역별로 만들어 내려준다. */
export type SectionActionProps = {
  isEditing: boolean;
  /** 다른 영역을 수정하는 중이면 이 영역은 수정을 시작할 수 없다. */
  disabled: boolean;
  /** 저장 요청이 진행 중이면 중복 요청되지 않도록 버튼을 잠근다. */
  isSaving: boolean;
  onEdit: () => void;
  onCancel: () => void;
  onSave: () => void;
  /**
   * 아코디언으로 쓸 때의 펼침 상태. 넘기지 않으면 항상 펼쳐진 것으로 본다.
   * (LMS 학생 상세처럼 접기가 없는 화면)
   */
  isOpen?: boolean;
  onToggle?: () => void;
  /** 수정 버튼 문구.  생략하면 '수정하기'로 쓴다. */
  editLabel?: string;
  /** 저장 버튼 문구. 입력 즉시 반영되는 영역은 '완료'처럼 바꿔 쓴다. */
  saveLabel?: string;
  /** 취소 버튼을 숨긴다. 되돌릴 값이 없는 영역에 쓴다. */
  hideCancel?: boolean;
};

/**
 * 영역 제목 + 해당 영역의 수정/저장 버튼.
 * 아코디언이면 제목 줄이 펼침 버튼이 되고, 본문은 `AccordionPanel` 로 감싼다.
 */
const SectionHeader = ({
  title,
  isEditing,
  disabled,
  isSaving,
  onEdit,
  onCancel,
  onSave,
  isOpen,
  onToggle,
  editLabel = '수정하기',
  saveLabel = '저장하기',
  hideCancel,
}: { title: string } & SectionActionProps) => {
  const isAccordion = !!onToggle;
  // 접기가 없는 화면은 항상 펼쳐진 상태로 본다.
  const opened = isOpen ?? true;

  const heading = (
    <div className="flex min-w-0 items-center gap-2">
      {isAccordion && (
        <ChevronDownIcon
          className={`size-5 shrink-0 text-gray7 transition-transform ${
            opened ? 'rotate-180' : ''
          }`}
        />
      )}
      <TextWinds type="title_h3" xlType="title_h3">
        {title}
      </TextWinds>
    </div>
  );

  const actions = (
    // 헤더 전체가 펼침 버튼이므로 버튼 클릭이 접힘으로 이어지지 않게 막는다.
    <div className="flex shrink-0 gap-2" onClick={(e) => e.stopPropagation()}>
      {isEditing ? (
        <>
          {!hideCancel && (
            <HFbutton
              type="primary-outlined"
              onClick={onCancel}
              height={36}
              width="auto"
              disabled={isSaving}
            >
              취소
            </HFbutton>
          )}
          <HFbutton
            onClick={onSave}
            height={36}
            width="auto"
            disabled={isSaving}
          >
            {isSaving ? '저장 중...' : saveLabel}
          </HFbutton>
        </>
      ) : (
        <HFbutton onClick={onEdit} height={36} width="auto" disabled={disabled}>
          {editLabel}
        </HFbutton>
      )}
    </div>
  );

  if (!isAccordion) {
    return (
      <div className="flex items-center justify-between gap-2">
        {heading}
        {actions}
      </div>
    );
  }

  return (
    <div
      className="flex cursor-pointer select-none items-center justify-between gap-2"
      onClick={onToggle}
      role="button"
      tabIndex={0}
      aria-expanded={opened}
      onKeyDown={(e) => {
        if (e.key !== 'Enter' && e.key !== ' ') return;
        e.preventDefault();
        onToggle?.();
      }}
    >
      {heading}
      {/* 접힌 상태에서는 수정 버튼을 감춰 제목 줄만 보이게 한다. */}
      {opened && actions}
    </div>
  );
};

export default SectionHeader;
